import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import AedPrice from '../components/AedPrice';
import './Checkout.css';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const cardStyle = {
  style: {
    base: {
      color: '#333',
      fontFamily: 'Cairo, Inter, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626'
    }
  },
  hidePostalCode: true
};

// Payment Form Component
const PaymentForm = ({ order, onSuccess, settings }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t, language, isRTL } = useLanguage();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [transferScreenshot, setTransferScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  // Track screen size for responsive copy buttons
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine default payment method based on what's enabled
  const getDefaultPaymentMethod = () => {
    if (settings.codEnabled) return 'cod';
    if (settings.bankTransferEnabled) return 'bank_transfer';
    if (settings.creditCardEnabled) return 'card';
    return 'cod'; // Fallback
  };

  const [paymentMethod, setPaymentMethod] = useState(getDefaultPaymentMethod());

  // Update payment method when settings change
  React.useEffect(() => {
    const defaultMethod = getDefaultPaymentMethod();
    // If current method is disabled, switch to default
    if ((paymentMethod === 'cod' && !settings.codEnabled) ||
        (paymentMethod === 'bank_transfer' && !settings.bankTransferEnabled) ||
        (paymentMethod === 'card' && !settings.creditCardEnabled)) {
      setPaymentMethod(defaultMethod);
    }
  }, [settings]);

  const haptic = () => {
    try { navigator.vibrate && navigator.vibrate(10); } catch(e) {}
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text.replace(/\s/g, '')); // Remove spaces for IBAN
    haptic();
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatPrice = (amount) => <AedPrice amount={amount} />;

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError('');
    }
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError(language === 'ar' ? 'يرجى اختيار صورة صالحة' : 'Please select a valid image');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(language === 'ar' ? 'حجم الصورة يجب أن يكون أقل من 5 ميجابايت' : 'Image size must be less than 5MB');
        return;
      }
      setTransferScreenshot(file);
      setScreenshotPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const removeScreenshot = () => {
    setTransferScreenshot(null);
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
    }
    setScreenshotPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setProcessing(true);

    try {
      if (paymentMethod === 'card') {
        if (!stripe || !elements) {
          setError('Payment system not ready');
          setProcessing(false);
          return;
        }

        // Create payment intent
        const intentResponse = await fetch('/api/payments/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: order.total,
            currency: 'aed',
            orderId: order.id
          })
        });

        const { clientSecret, error: intentError } = await intentResponse.json();

        if (intentError) {
          setError(intentError);
          setProcessing(false);
          return;
        }

        // Confirm card payment
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: order.shippingAddress?.fullName || order.customerName,
              phone: order.shippingAddress?.phone
            }
          }
        });

        if (stripeError) {
          setError(stripeError.message);
          setProcessing(false);
          return;
        }

        if (paymentIntent.status === 'succeeded') {
          // Update order payment status
          const updateResponse = await fetch(`/api/orders/public/${order.id}/payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              paymentIntentId: paymentIntent.id,
              paymentMethod: 'card',
              paymentStatus: 'paid'
            })
          });

          if (updateResponse.ok) {
            onSuccess();
          } else {
            setError('Payment successful but failed to update order');
          }
        }
      } else if (paymentMethod === 'bank_transfer') {
        // Bank transfer - upload screenshot and confirm
        if (!transferScreenshot) {
          setError(language === 'ar' ? 'يرجى رفع صورة إيصال التحويل' : 'Please upload the transfer receipt screenshot');
          setProcessing(false);
          return;
        }

        setUploadingScreenshot(true);

        // Upload screenshot first
        const formData = new FormData();
        formData.append('screenshot', transferScreenshot);
        formData.append('orderId', order.id);

        const uploadResponse = await fetch(`/api/orders/public/${order.id}/upload-screenshot`, {
          method: 'POST',
          body: formData
        });

        if (!uploadResponse.ok) {
          const uploadError = await uploadResponse.json();
          setError(uploadError.error || 'Failed to upload screenshot');
          setUploadingScreenshot(false);
          setProcessing(false);
          return;
        }

        const { screenshotUrl } = await uploadResponse.json();
        setUploadingScreenshot(false);

        // Update order with bank transfer info
        const updateResponse = await fetch(`/api/orders/public/${order.id}/payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentMethod: 'bank_transfer',
            paymentStatus: 'pending_verification',
            transferScreenshot: screenshotUrl
          })
        });

        if (updateResponse.ok) {
          onSuccess();
        } else {
          setError('Failed to confirm order');
        }
      } else {
        // Cash on delivery - just confirm
        const updateResponse = await fetch(`/api/orders/public/${order.id}/payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentMethod: 'cod',
            paymentStatus: 'pending'
          })
        });

        if (updateResponse.ok) {
          onSuccess();
        } else {
          setError('Failed to confirm order');
        }
      }
    } catch (err) {
      setError(err.message || 'Payment failed');
    } finally {
      setProcessing(false);
      setUploadingScreenshot(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 style={{ marginBottom: '20px' }}>{t('paymentMethod')}</h3>

      <div className="payment-methods">
        {settings.codEnabled && (
          <label className={`payment-option ${paymentMethod === 'cod' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="paymentMethod"
              value="cod"
              checked={paymentMethod === 'cod'}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            <span className="payment-option-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg></span>
            <span className="payment-option-text">
              <strong>{t('cashOnDelivery')}</strong>
              <small>{t('payWhenReceive')}</small>
            </span>
          </label>
        )}

        {settings.bankTransferEnabled && (
          <label className={`payment-option ${paymentMethod === 'bank_transfer' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="paymentMethod"
              value="bank_transfer"
              checked={paymentMethod === 'bank_transfer'}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            <span className="payment-option-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/></svg></span>
            <span className="payment-option-text">
              <strong>{language === 'ar' ? 'تحويل بنكي' : 'Bank Transfer'}</strong>
              <small>{language === 'ar' ? 'تحويل إلى حسابنا البنكي' : 'Transfer to our bank account'}</small>
            </span>
          </label>
        )}

        {settings.creditCardEnabled && stripePromise && (
          <label className={`payment-option ${paymentMethod === 'card' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="paymentMethod"
              value="card"
              checked={paymentMethod === 'card'}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            <span className="payment-option-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg></span>
            <span className="payment-option-text">
              <strong>{t('creditCard')}</strong>
              <small>{t('securePayment')}</small>
            </span>
          </label>
        )}
      </div>

      {paymentMethod === 'bank_transfer' && (
        <div style={{ marginTop: '20px' }}>
          {/* Bank Details */}
          <div style={{
            background: 'var(--gray-50)',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid var(--gray-200)'
          }}>
            <h4 style={{ marginBottom: '16px', fontSize: '17px', color: 'var(--gray-900)', fontWeight: '700' }}>
              {language === 'ar' ? 'تفاصيل الحساب البنكي' : 'Bank Account Details'}
            </h4>
            <div style={{ display: 'grid', gap: '12px', fontSize: '14px' }}>
              {/* Bank Name Card */}
              <div style={{
                background: 'white',
                padding: '14px 16px',
                borderRadius: '10px',
                border: '1px solid var(--gray-200)'
              }}>
                <div style={{ color: 'var(--gray-500)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {language === 'ar' ? 'اسم البنك' : 'Bank Name'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ fontWeight: '600', color: 'var(--gray-800)' }}>
                    {language === 'ar' ? settings.bankNameAr : settings.bankName}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(language === 'ar' ? settings.bankNameAr : settings.bankName, 'bankName')}
                    style={{
                      background: copiedField === 'bankName' ? '#d1fae5' : 'var(--gray-100)',
                      color: copiedField === 'bankName' ? '#065f46' : 'var(--gray-600)',
                      border: '1px solid',
                      borderColor: copiedField === 'bankName' ? '#a7f3d0' : 'var(--gray-200)',
                      borderRadius: '6px',
                      padding: isMobile ? '8px' : '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s',
                      flexShrink: 0
                    }}
                  >
                    {copiedField === 'bankName' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                    )}
                    {!isMobile && (copiedField === 'bankName' ? (language === 'ar' ? 'تم' : 'Copied') : (language === 'ar' ? 'نسخ' : 'Copy'))}
                  </button>
                </div>
              </div>

              {/* Account Name Card */}
              <div style={{
                background: 'white',
                padding: '14px 16px',
                borderRadius: '10px',
                border: '1px solid var(--gray-200)'
              }}>
                <div style={{ color: 'var(--gray-500)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {language === 'ar' ? 'اسم الحساب' : 'Account Name'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ fontWeight: '600', color: 'var(--gray-800)' }}>
                    {language === 'ar' ? settings.accountNameAr : settings.accountName}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(language === 'ar' ? settings.accountNameAr : settings.accountName, 'accountName')}
                    style={{
                      background: copiedField === 'accountName' ? '#d1fae5' : 'var(--gray-100)',
                      color: copiedField === 'accountName' ? '#065f46' : 'var(--gray-600)',
                      border: '1px solid',
                      borderColor: copiedField === 'accountName' ? '#a7f3d0' : 'var(--gray-200)',
                      borderRadius: '6px',
                      padding: isMobile ? '8px' : '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s',
                      flexShrink: 0
                    }}
                  >
                    {copiedField === 'accountName' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                    )}
                    {!isMobile && (copiedField === 'accountName' ? (language === 'ar' ? 'تم' : 'Copied') : (language === 'ar' ? 'نسخ' : 'Copy'))}
                  </button>
                </div>
              </div>

              {/* Account Number Card */}
              <div style={{
                background: 'white',
                padding: '14px 16px',
                borderRadius: '10px',
                border: '1px solid var(--gray-200)'
              }}>
                <div style={{ color: 'var(--gray-500)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {language === 'ar' ? 'رقم الحساب' : 'Account Number'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ fontWeight: '600', color: 'var(--gray-800)', fontFamily: 'monospace' }}>
                    {settings.accountNumber}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(settings.accountNumber, 'accountNumber')}
                    style={{
                      background: copiedField === 'accountNumber' ? '#d1fae5' : 'var(--gray-100)',
                      color: copiedField === 'accountNumber' ? '#065f46' : 'var(--gray-600)',
                      border: '1px solid',
                      borderColor: copiedField === 'accountNumber' ? '#a7f3d0' : 'var(--gray-200)',
                      borderRadius: '6px',
                      padding: isMobile ? '8px' : '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s',
                      flexShrink: 0
                    }}
                  >
                    {copiedField === 'accountNumber' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                    )}
                    {!isMobile && (copiedField === 'accountNumber' ? (language === 'ar' ? 'تم' : 'Copied') : (language === 'ar' ? 'نسخ' : 'Copy'))}
                  </button>
                </div>
              </div>

              {/* IBAN Card */}
              <div style={{
                background: 'white',
                padding: '14px 16px',
                borderRadius: '10px',
                border: '1px solid var(--gray-200)'
              }}>
                <div style={{ color: 'var(--gray-500)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  IBAN
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: '600', color: 'var(--gray-800)', fontFamily: 'monospace', letterSpacing: '1px', wordBreak: 'break-all' }}>
                    {settings.iban}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(settings.iban, 'iban')}
                    style={{
                      background: copiedField === 'iban' ? '#d1fae5' : 'var(--gray-100)',
                      color: copiedField === 'iban' ? '#065f46' : 'var(--gray-600)',
                      border: '1px solid',
                      borderColor: copiedField === 'iban' ? '#a7f3d0' : 'var(--gray-200)',
                      borderRadius: '6px',
                      padding: isMobile ? '8px' : '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s',
                      flexShrink: 0
                    }}
                  >
                    {copiedField === 'iban' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                    )}
                    {!isMobile && (copiedField === 'iban' ? (language === 'ar' ? 'تم' : 'Copied') : (language === 'ar' ? 'نسخ' : 'Copy'))}
                  </button>
                </div>
              </div>
            </div>
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: '#fef3c7',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#92400e'
            }}>
              {language === 'ar'
                ? <span>يرجى تحويل المبلغ {formatPrice(order.total)} وإرفاق صورة إيصال التحويل حتى نتمكن من معالجة طلبك</span>
                : <span>Please transfer {formatPrice(order.total)} and attach the transfer receipt so we can process your order</span>
              }
            </div>
          </div>

          {/* Screenshot Upload */}
          <div className="form-group">
            <label className="form-label">
              {language === 'ar' ? 'صورة إيصال التحويل' : 'Transfer Receipt Screenshot'} <span style={{ color: 'red' }}>*</span>
            </label>

            {!screenshotPreview ? (
              <div
                style={{
                  border: '2px dashed var(--gray-300)',
                  borderRadius: '12px',
                  padding: '30px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                  background: 'var(--gray-50)'
                }}
                onClick={() => document.getElementById('screenshot-input').click()}
              >
                <input
                  id="screenshot-input"
                  type="file"
                  accept="image/*"
                  onChange={handleScreenshotChange}
                  style={{ display: 'none' }}
                />
                <div style={{ marginBottom: '10px' }}><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg></div>
                <p style={{ margin: 0, color: 'var(--gray-600)' }}>
                  {language === 'ar' ? 'اضغط لرفع صورة الإيصال' : 'Click to upload receipt image'}
                </p>
                <p style={{ margin: '5px 0 0', color: 'var(--gray-400)', fontSize: '13px' }}>
                  {language === 'ar' ? 'PNG, JPG حتى 5 ميجابايت' : 'PNG, JPG up to 5MB'}
                </p>
              </div>
            ) : (
              <div style={{
                position: 'relative',
                border: '1px solid var(--gray-200)',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                <img
                  src={screenshotPreview}
                  alt="Receipt"
                  style={{
                    width: '100%',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    background: 'var(--gray-50)'
                  }}
                />
                <button
                  type="button"
                  onClick={removeScreenshot}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
                <div style={{
                  padding: '12px',
                  background: '#d1fae5',
                  color: '#065f46',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>✓</span>
                  {language === 'ar' ? 'تم تحميل الصورة بنجاح' : 'Image uploaded successfully'}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {paymentMethod === 'card' && stripePromise && (
        <div className="card-element-container" style={{ marginTop: '20px' }}>
          <label className="form-label">{t('cardDetails')}</label>
          <div className="card-element-wrapper" dir="ltr" style={{ textAlign: 'left' }}>
            <CardElement options={cardStyle} onChange={handleCardChange} />
          </div>
          <p className="card-hint">
            Test card: 4242 4242 4242 4242, any future date, any CVC
          </p>
        </div>
      )}

      {error && <div className="alert alert-danger" style={{ marginTop: '20px' }}>{error}</div>}

      <button
        type="submit"
        className="btn btn-primary btn-lg btn-block"
        style={{ marginTop: '20px' }}
        disabled={processing || uploadingScreenshot || (paymentMethod === 'card' && !cardComplete) || (paymentMethod === 'bank_transfer' && !transferScreenshot)}
      >
        {processing || uploadingScreenshot ? (
          uploadingScreenshot
            ? (language === 'ar' ? 'جاري رفع الصورة...' : 'Uploading image...')
            : t('processing')
        ) : (
          paymentMethod === 'card'
            ? <span>{t('payNow')} {formatPrice(order.total)}</span>
            : paymentMethod === 'bank_transfer'
              ? (language === 'ar' ? 'تأكيد التحويل البنكي' : 'Confirm Bank Transfer')
              : t('placeOrder')
        )}
      </button>
    </form>
  );
};

const EMIRATES = [
  { id: 'abu_dhabi', name: 'Abu Dhabi', nameAr: 'أبوظبي' },
  { id: 'al_ain', name: 'Al Ain', nameAr: 'العين' },
  { id: 'dubai', name: 'Dubai', nameAr: 'دبي' },
  { id: 'sharjah', name: 'Sharjah', nameAr: 'الشارقة' },
  { id: 'ajman', name: 'Ajman', nameAr: 'عجمان' },
  { id: 'umm_al_quwain', name: 'Umm Al Quwain', nameAr: 'أم القيوين' },
  { id: 'ras_al_khaimah', name: 'Ras Al Khaimah', nameAr: 'رأس الخيمة' },
  { id: 'fujairah', name: 'Fujairah', nameAr: 'الفجيرة' }
];

// Main PayOrder Component
const PayOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language, isRTL, setLanguage } = useLanguage();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [needsDetails, setNeedsDetails] = useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    emirate: ''
  });
  const [detailsError, setDetailsError] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [settings, setSettings] = useState({
    codEnabled: true,
    bankTransferEnabled: true,
    creditCardEnabled: false,
    bankName: 'Emirates NBD',
    bankNameAr: 'الإمارات دبي الوطني',
    accountName: 'Company Name LLC',
    accountNameAr: 'اسم الشركة ذ.م.م',
    iban: 'AE12 0260 0010 1234 5678 901',
    accountNumber: '1234567890',
    showEmailOnPayPage: false
  });

  useEffect(() => {
    fetchOrder();
    fetchSettings();
    window.scrollTo(0, 0);
    // Set language to Arabic by default for payment links
    setLanguage('ar');
  }, [id]);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/settings/public');
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  // Pre-fill with logged-in user data
  useEffect(() => {
    if (user && needsDetails) {
      setCustomerDetails(prev => ({
        ...prev,
        fullName: prev.fullName || user.name || '',
        email: prev.email || user.email || ''
      }));
    }
  }, [user, needsDetails]);

  const fetchOrder = async () => {
    try {
      const response = await fetch(`/api/orders/public/${id}`);
      if (response.ok) {
        const data = await response.json();

        // Check if order is already paid or completed
        if (data.paymentStatus === 'paid' || data.status === 'pending' || data.status === 'processing' || data.status === 'shipped' || data.status === 'delivered') {
          setSuccess(true);
          setOrder(data);
          setLoading(false);
          return;
        }

        // Check if order needs customer details
        const hasShippingDetails = data.shippingAddress &&
          data.shippingAddress.fullName &&
          data.shippingAddress.address &&
          data.shippingAddress.phone &&
          data.shippingAddress.emirate;

        if (!hasShippingDetails) {
          setNeedsDetails(true);
          // Pre-fill with existing data if available
          setCustomerDetails({
            fullName: data.shippingAddress?.fullName || data.customerName || '',
            email: data.customerEmail || '',
            phone: data.shippingAddress?.phone || data.customerPhone || '',
            address: data.shippingAddress?.address || '',
            city: data.shippingAddress?.city || '',
            emirate: data.shippingAddress?.emirate || ''
          });
        }

        setOrder(data);
      } else {
        const data = await response.json();
        setError(data.error || 'Order not found');
      }
    } catch (err) {
      setError('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const handleDetailsChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Convert Arabic/Persian digits to English for phone field
    if (name === 'phone') {
      processedValue = value.replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
                           .replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));
    }

    setCustomerDetails(prev => ({ ...prev, [name]: processedValue }));
  };

  const handleSaveDetails = async (e) => {
    e.preventDefault();
    setDetailsError('');

    // Validate required fields
    if (!customerDetails.fullName.trim()) {
      setDetailsError(language === 'ar' ? 'الاسم مطلوب' : 'Name is required');
      return;
    }
    if (!customerDetails.phone.trim()) {
      setDetailsError(language === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone is required');
      return;
    }
    // Email is optional - no validation needed
    if (!customerDetails.address.trim()) {
      setDetailsError(language === 'ar' ? 'العنوان مطلوب' : 'Address is required');
      return;
    }
    if (!customerDetails.emirate) {
      setDetailsError(language === 'ar' ? 'الإمارة / المدينة مطلوبة' : 'Emirate / City is required');
      return;
    }

    setSavingDetails(true);

    try {
      const response = await fetch(`/api/orders/public/${id}/details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.id || null,
          customerName: customerDetails.fullName,
          customerEmail: customerDetails.email,
          customerPhone: customerDetails.phone,
          shippingAddress: {
            fullName: customerDetails.fullName,
            address: customerDetails.address,
            city: customerDetails.city,
            emirate: customerDetails.emirate,
            phone: customerDetails.phone,
            country: 'UAE'
          }
        })
      });

      if (response.ok) {
        const updatedOrder = await response.json();
        setOrder(updatedOrder);
        setNeedsDetails(false);
      } else {
        const data = await response.json();
        setDetailsError(data.error || 'Failed to save details');
      }
    } catch (err) {
      setDetailsError(language === 'ar' ? 'فشل في حفظ البيانات' : 'Failed to save details');
    } finally {
      setSavingDetails(false);
    }
  };

  const formatPrice = (amount) => <AedPrice amount={amount} />;

  const getProductName = (item) => {
    return language === 'ar' && item.nameAr ? item.nameAr : item.name;
  };

  const handleSuccess = () => {
    setSuccess(true);
  };

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ marginBottom: '20px' }}><svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg></div>
            <h2>{language === 'ar' ? 'خطأ' : 'Error'}</h2>
            <p style={{ color: 'var(--gray-600)' }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={`checkout-page ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ marginBottom: '20px' }}><svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>
            <h2>{t('orderPlaced')}</h2>
            <p style={{ color: 'var(--gray-600)', marginBottom: '10px' }}>{t('orderPlacedDesc')}</p>
            <p style={{ color: 'var(--gray-600)', marginBottom: '20px' }}>
              {language === 'ar' ? 'رقم الطلب:' : 'Order ID:'} <strong>{order?.id}</strong>
            </p>

            {/* Disclaimer */}
            <div style={{
              background: '#fef3c7',
              padding: '16px 24px',
              borderRadius: '12px',
              maxWidth: '500px',
              margin: '0 auto 30px',
              border: '1px solid #fcd34d'
            }}>
              <p style={{ margin: 0, color: '#92400e', fontSize: '14px', lineHeight: 1.6 }}>
                {language === 'ar'
                  ? 'تم استلام طلبك. سنقوم بمعالجة وشحن طلبك بعد تأكيد الدفع. في حال عدم توفر أي منتج بعد تأكيد الدفع، سيتم استرداد المبلغ.'
                  : 'Your order request has been received. We will process and ship your items once payment is confirmed. If any items become unavailable after payment confirmation, you will be refunded.'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show details form if customer info is missing
  if (needsDetails) {
    return (
      <div className={`checkout-page ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container">
          <div className="page-header">
            <h1>{language === 'ar' ? 'بيانات التوصيل' : 'Delivery Details'}</h1>
            <p>{language === 'ar' ? 'طلب رقم:' : 'Order:'} {order?.id}</p>
          </div>

          <div className="checkout-layout">
            <div className="checkout-form">
              <form onSubmit={handleSaveDetails}>
                <h3 style={{ marginBottom: '20px' }}>
                  {language === 'ar' ? 'معلومات التوصيل' : 'Shipping Information'}
                </h3>

                <div className="form-group">
                  <label className="form-label">
                    {language === 'ar' ? 'الاسم الكامل' : 'Full Name'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    className="form-input"
                    value={customerDetails.fullName}
                    onChange={handleDetailsChange}
                    placeholder={language === 'ar' ? 'الاسم الكامل' : 'Full name'}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {language === 'ar' ? 'رقم الهاتف' : 'Phone'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-input"
                    value={customerDetails.phone}
                    onChange={handleDetailsChange}
                    placeholder="05XXXXXXXX"
                    inputMode="numeric"
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                    required
                  />
                </div>

                {settings.showEmailOnPayPage && (
                <div className="form-group">
                  <label className="form-label">
                    {language === 'ar' ? 'البريد الإلكتروني' : 'Email'} <span style={{ color: '#9ca3af', fontSize: '11px', fontWeight: '400' }}>({language === 'ar' ? 'اختياري' : 'optional'})</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={customerDetails.email}
                    onChange={handleDetailsChange}
                    placeholder={language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                  />
                </div>
                )}

                <div className="form-group">
                  <label className="form-label">
                    {language === 'ar' ? 'العنوان' : 'Address'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    className="form-input"
                    value={customerDetails.address}
                    onChange={handleDetailsChange}
                    placeholder={language === 'ar' ? 'عنوان التوصيل' : 'Delivery address'}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label className="form-label">
                      {language === 'ar' ? 'المدينة' : 'City'}
                    </label>
                    <input
                      type="text"
                      name="city"
                      className="form-input"
                      value={customerDetails.city}
                      onChange={handleDetailsChange}
                      placeholder={language === 'ar' ? 'المدينة' : 'City'}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {language === 'ar' ? 'الإمارة / المدينة' : 'Emirate / City'} <span style={{ color: 'red' }}>*</span>
                    </label>
                    <select
                      name="emirate"
                      className="form-input"
                      value={customerDetails.emirate}
                      onChange={handleDetailsChange}
                      required
                    >
                      <option value="">{language === 'ar' ? 'اختر الإمارة / المدينة' : 'Select Emirate / City'}</option>
                      {EMIRATES.map(emirate => (
                        <option key={emirate.id} value={emirate.id}>
                          {language === 'ar' ? emirate.nameAr : emirate.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {detailsError && (
                  <div className="alert alert-danger" style={{ marginTop: '20px' }}>
                    {detailsError}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary btn-lg btn-block"
                  style={{ marginTop: '20px' }}
                  disabled={savingDetails}
                >
                  {savingDetails
                    ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                    : (language === 'ar' ? 'متابعة للدفع' : 'Continue to Payment')
                  }
                </button>
              </form>
            </div>

            {/* Order Summary */}
            <div className="order-summary" style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid var(--gray-200)' }}>
              <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>{t('orderSummary')}</h3>

              <div className="summary-items" style={{ marginBottom: '20px' }}>
                {order?.items?.map((item, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    background: 'var(--gray-50)',
                    borderRadius: '12px',
                    marginBottom: '12px'
                  }}>
                    {item.hideImage ? (
                      <div style={{
                        width: '70px',
                        height: '70px',
                        background: '#e5e7eb',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px'
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                      </div>
                    ) : (
                      <img
                        src={item.image}
                        alt={item.name}
                        style={{
                          width: '70px',
                          height: '70px',
                          objectFit: 'cover',
                          borderRadius: '10px',
                          border: '1px solid var(--gray-200)'
                        }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', marginBottom: '6px', fontSize: '15px' }}>
                        {getProductName(item)}
                      </div>
                      <div style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                        {language === 'ar' ? 'الكمية:' : 'Quantity:'} {item.quantity}
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ color: 'var(--primary)', fontWeight: '600' }}>{formatPrice(item.price * item.quantity)}</span>
                          {item.onSale && item.originalPrice && item.originalPrice !== item.price && (
                            <span style={{ fontWeight: '700', color: '#dc2626', fontSize: '12px' }}>
                              -{Math.round((1 - item.price / item.originalPrice) * 100)}%
                            </span>
                          )}
                        </div>
                        {item.onSale && item.originalPrice && item.originalPrice !== item.price && (
                          <div style={{ fontSize: '12px', color: '#9ca3af', textDecoration: 'line-through', marginTop: '2px' }}>
                            {formatPrice(item.originalPrice * item.quantity)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: 'var(--gray-600)' }}>
                  <span>{t('subtotal')}</span>
                  <span>{formatPrice(order?.subtotal || 0)}</span>
                </div>
                {((order?.payToDriver && (order?.shipping ?? 0) > 0) || (settings.forcePayToDriver && (order?.shipping ?? 0) > 0)) ? (
                  <div style={{
                    marginBottom: '12px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    fontSize: '13px',
                    color: '#92400e'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '16px' }}>🚚</span>
                      <span style={{ fontWeight: '600' }}>
                        {language === 'ar'
                          ? 'المبلغ أعلاه غير شامل رسوم التوصيل'
                          : 'The above amount does not include delivery fee'}
                      </span>
                    </div>
                    <div style={{ marginTop: '6px', paddingRight: isRTL ? '24px' : '0', paddingLeft: isRTL ? '0' : '24px', fontSize: '12px', lineHeight: '1.6' }}>
                      {settings.forcePayToDriver ? (
                        <span>
                          {language === 'ar'
                            ? 'رسوم التوصيل تُحدد حسب المنطقة وتُدفع نقداً للسائق عند الاستلام'
                            : 'Delivery fee is determined by your area and paid in cash to the driver upon delivery'}
                        </span>
                      ) : (
                        <span>
                          {language === 'ar'
                            ? `رسوم التوصيل ${Math.round(order.shipping)} درهم — تُدفع نقداً للسائق عند الاستلام`
                            : `Delivery fee AED ${Math.round(order.shipping)} — to be paid in cash to the driver upon delivery`}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', color: 'var(--gray-600)' }}>
                    <span>{t('shipping')}</span>
                    <span>{formatPrice(order?.shipping ?? 0)}</span>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: '12px',
                  borderTop: '2px solid var(--gray-300)',
                  fontWeight: '700',
                  fontSize: '18px'
                }}>
                  <span>{t('total')}</span>
                  <span style={{ color: 'var(--primary)' }}>{formatPrice(order?.total || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`checkout-page ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container">
        <div className="page-header">
          <h1>{language === 'ar' ? 'إتمام الطلب' : 'Complete Order'}</h1>
          <p>{language === 'ar' ? 'طلب رقم:' : 'Order:'} {order?.id}</p>
        </div>

        <div className="checkout-layout">
          {/* Payment Form */}
          <div className="checkout-form">
            {/* Shipping Info (Read Only) */}
            <div style={{
              background: 'var(--gray-50)',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '30px',
              border: '1px solid var(--gray-200)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '16px' }}>{t('shippingAddress')}</h3>
                <button
                  onClick={() => setNeedsDetails(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '4px 8px',
                    borderRadius: '6px'
                  }}
                >
                  {language === 'ar' ? 'تعديل' : 'Edit'}
                </button>
              </div>
              <p style={{ margin: 0, lineHeight: 1.8, color: 'var(--gray-700)' }}>
                <strong>{order?.shippingAddress?.fullName || order?.customerName}</strong><br />
                {order?.shippingAddress?.address && <>{order.shippingAddress.address}<br /></>}
                {order?.shippingAddress?.city && order?.shippingAddress?.emirate && (
                  <>{order.shippingAddress.city}, {order.shippingAddress.emirate}<br /></>
                )}
                {order?.shippingAddress?.phone || order?.customerPhone}
              </p>
            </div>

            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <PaymentForm order={order} onSuccess={handleSuccess} settings={settings} />
              </Elements>
            ) : (
              <PaymentForm order={order} onSuccess={handleSuccess} settings={settings} />
            )}
          </div>

          {/* Order Summary */}
          <div className="order-summary" style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid var(--gray-200)' }}>
            <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>{t('orderSummary')}</h3>

            <div style={{ marginBottom: '20px' }}>
              {order?.items?.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px',
                  background: 'var(--gray-50)',
                  borderRadius: '12px',
                  marginBottom: '12px'
                }}>
                  {item.hideImage ? (
                    <div style={{
                      width: '70px',
                      height: '70px',
                      background: '#e5e7eb',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                    </div>
                  ) : (
                    <img
                      src={item.image}
                      alt={item.name}
                      style={{
                        width: '70px',
                        height: '70px',
                        objectFit: 'cover',
                        borderRadius: '10px',
                        border: '1px solid var(--gray-200)'
                      }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', marginBottom: '6px', fontSize: '15px' }}>
                      {getProductName(item)}
                    </div>
                    <div style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                      {language === 'ar' ? 'الكمية:' : 'Quantity:'} {item.quantity}
                    </div>
                    <div style={{ color: 'var(--primary)', fontWeight: '600', marginTop: '4px' }}>
                      {formatPrice(item.price * item.quantity)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: 'var(--gray-600)' }}>
                <span>{t('subtotal')}</span>
                <span>{formatPrice(order?.subtotal || 0)}</span>
              </div>
              {(order?.payToDriver && (order?.shipping ?? 0) > 0) || settings.forcePayToDriver ? (
                <div style={{
                  marginBottom: '12px',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  background: '#fffbeb',
                  border: '1px solid #fcd34d',
                  fontSize: '13px',
                  color: '#92400e'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: (order?.payToDriver && (order?.shipping ?? 0) > 0) ? '6px' : '0' }}>
                    <span style={{ fontSize: '16px' }}>🚚</span>
                    <span style={{ fontWeight: '600' }}>
                      {language === 'ar'
                        ? 'يرجى العلم بأنه غير شامل رسوم التوصيل - تُدفع مباشرة للسائق'
                        : 'Delivery fee not included - pay directly to the driver'}
                    </span>
                  </div>
                  {order?.payToDriver && (order?.shipping ?? 0) > 0 ? (
                    <div style={{ paddingRight: isRTL ? '0' : '24px', paddingLeft: isRTL ? '24px' : '0', marginRight: isRTL ? '24px' : '0', marginLeft: isRTL ? '0' : '24px' }}>
                      {language === 'ar'
                        ? `رسوم التوصيل ${Math.round(order.shipping)} درهم`
                        : `Delivery fee AED ${Math.round(order.shipping)}`}
                    </div>
                  ) : settings.forcePayToDriver ? (
                    <div style={{ paddingRight: isRTL ? '0' : '24px', paddingLeft: isRTL ? '24px' : '0', marginRight: isRTL ? '24px' : '0', marginLeft: isRTL ? '0' : '24px', fontSize: '12px', marginTop: '4px' }}>
                      {language === 'ar'
                        ? 'تختلف رسوم التوصيل حسب المنطقة'
                        : 'Delivery fee varies based on your area'}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', color: 'var(--gray-600)' }}>
                  <span>{t('shipping')}</span>
                  <span>{formatPrice(order?.shipping ?? 0)}</span>
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '12px',
                borderTop: '2px solid var(--gray-300)',
                fontWeight: '700',
                fontSize: '18px'
              }}>
                <span>{t('total')}</span>
                <span style={{ color: 'var(--primary)' }}>{formatPrice(order?.total || 0)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayOrder;
